import { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useTranslation } from "react-i18next"
import { CardNumberElement, useElements, useStripe } from "@stripe/react-stripe-js"
import {
  attachUserPaymentMethod,
  cleanedPaymentMethods,
  failedPaymentMethods,
  getPaymentMethods,
  isPaypalConnectedSelector,
  isStripeConnectedSelector,
  paymentMethodsLoadingSelector,
  paymentMethodsSelector,
  requestedPaymentMethods,
  selectedCardLast4Selector,
  selectedPaymentMethodIdSelector,
  setSelectedPaymentMethodId
} from "store/user"
import { captureGuideBookingIncomeRecord, captureGuideBookingTipRecord, getClientBookingDetails, getFeeSummary, takeGuideBookingPayment } from "store/bookings"
import { toaster } from "components/common/Toast"
import { receiveErrors, resetErrors } from "modules/errors/reducer"
import { isFeatureEnabled } from "config/features"
import { useStripeIssue } from "hooks/useStripeIssue"
import { LINK_PROVIDERS, PAYMENT_TABS, paymentProvider } from "./constants"

export const useTakePaymentForm = ({ remainingBalance, bookingUUID, bookingId, onClose, clientEmail, form, modalForm }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const stripe = useStripe()
  const elements = useElements()

  const [activeTab, setActiveTab] = useState(PAYMENT_TABS.CARD)
  const [linkProvider, setLinkProvider] = useState(LINK_PROVIDERS.STRIPE)
  const [tipAmount, setTipAmount] = useState(0)
  const [isModalVisible, setIsModalVisible] = useState(false)
  const [isAddingCard, setIsAddingCard] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const isStripeConnected = useSelector(isStripeConnectedSelector)
  const isPaypalConnected = useSelector(isPaypalConnectedSelector)
  const { isStripeChargeable, stripeProblemMessage } = useStripeIssue()
  const paymentMethods = useSelector(paymentMethodsSelector)
  const paymentMethodsLoading = useSelector(paymentMethodsLoadingSelector)
  const selectedPaymentMethodId = useSelector(selectedPaymentMethodIdSelector)
  const selectedCardLast4 = useSelector(selectedCardLast4Selector)
  const clientId = useSelector((state) => state.bookings.clientId)
  const { guide } = useSelector((state) => state.user)
  const { data: feeSummaryData, loading: feeSummaryLoading } = useSelector((state) => state.bookings.feeSummary)

  useEffect(() => {
    dispatch(getFeeSummary())
  }, [dispatch])

  useEffect(() => {
    if (clientId) {
      dispatch(cleanedPaymentMethods())
      dispatch(getPaymentMethods(clientId))
    }
  }, [clientId, dispatch])

  // When Stripe can't charge (never connected, error, or under review), keep the user off the
  // Stripe-only paths: the Card tab and the Stripe link provider. Fall back to PayPal link when
  // available, otherwise cash.
  useEffect(() => {
    if (isStripeChargeable) return
    setLinkProvider(LINK_PROVIDERS.PAYPAL)
    const paypalLinkAvailable = isPaypalConnected && isFeatureEnabled("PAYPAL_SUPPORT")
    if (activeTab === PAYMENT_TABS.CARD) {
      setActiveTab(paypalLinkAvailable ? PAYMENT_TABS.LINK : PAYMENT_TABS.CASH)
    } else if (activeTab === PAYMENT_TABS.LINK && !paypalLinkAvailable) {
      setActiveTab(PAYMENT_TABS.CASH)
    }
  }, [isStripeChargeable]) // eslint-disable-line react-hooks/exhaustive-deps

  const submitCardPayment = async (baseAmount) => {
    if (!selectedPaymentMethodId) {
      toaster.error(t("client.payment.paymentMethodError"))
      return false
    }
    const response = await dispatch(
      takeGuideBookingPayment(bookingId, { amount: baseAmount, tip_amount: tipAmount, payment_provider: paymentProvider.card, isLink: false })
    )
    if (response?.client_secret) {
      const result = await stripe.confirmCardPayment(response.client_secret, { payment_method: selectedPaymentMethodId })
      if (result?.error) {
        toaster.error(result.error.message)
        return false
      }
      await dispatch(captureGuideBookingIncomeRecord(bookingId, response.income?.id))

      if (response.tip?.client_secret) {
        const tipResult = await stripe.confirmCardPayment(response.tip.client_secret, { payment_method: selectedPaymentMethodId })
        if (tipResult?.error) {
          toaster.error(tipResult.error.message)
          return false
        }
        await dispatch(captureGuideBookingTipRecord(bookingId, response.tip.tip_transaction_id))
      }

      toaster.success(`Card ending in ****${selectedCardLast4?.toString() ?? ""} has been charged`)
    }
    return true
  }

  const submitLinkPayment = async (baseAmount) => {
    const providerKey = linkProvider === LINK_PROVIDERS.STRIPE ? "link" : "paypalLink"
    const response = await dispatch(
      takeGuideBookingPayment(bookingId, { amount: baseAmount, tip_amount: tipAmount, payment_provider: paymentProvider[providerKey], isLink: true })
    )
    toaster.success(response?.message)
    return true
  }

  const submitOfflinePayment = async (baseAmount, values) => {
    const payload = activeTab === PAYMENT_TABS.OTHERS
      ? { amount: baseAmount, tip_amount: tipAmount, payment_provider: values.othersType, note: values.note }
      : { amount: baseAmount, tip_amount: tipAmount, payment_provider: "cash" }
    const response = await dispatch(takeGuideBookingPayment(bookingId, payload))
    if (response?.status === "confirmed") toaster.success("Payment recorded!")
    return true
  }

  const handleSubmit = async (amountValue) => {
    try {
      const values = await form.validateFields()
      const baseAmount = Number(amountValue) || 0

      // Defense in depth: the Stripe tabs are disabled in the UI, but that only removes the
      // affordance. Refuse to submit a Stripe charge here too, so toggling the disabled state via
      // dev tools can't push a payment through a Stripe account that can't charge. (The backend /
      // Stripe must still be the authoritative guard.)
      const usesStripe = activeTab === PAYMENT_TABS.CARD || (activeTab === PAYMENT_TABS.LINK && linkProvider === LINK_PROVIDERS.STRIPE)
      if (usesStripe && !isStripeChargeable) {
        toaster.error(stripeProblemMessage || t("form_validation.payment_provider_missing"))
        return
      }

      setIsSubmitting(true)

      let success = false
      if (activeTab === PAYMENT_TABS.CARD) success = await submitCardPayment(baseAmount)
      else if (activeTab === PAYMENT_TABS.LINK) success = await submitLinkPayment(baseAmount)
      else success = await submitOfflinePayment(baseAmount, values)

      if (!success) return

      dispatch(getClientBookingDetails(bookingUUID))
      form.resetFields()
      onClose()
    } catch (error) {
      console.error("Payment error:", error)
    } finally {
      setIsSubmitting(false)
    }
  }

  const createPaymentMethod = async () => {
    setIsAddingCard(true)
    try {
      const formData = await modalForm.validateFields()
      if (!stripe || !elements) return

      const cardNumberElement = elements.getElement(CardNumberElement)
      if (!cardNumberElement) return

      dispatch(requestedPaymentMethods())
      dispatch(resetErrors())

      const { paymentMethod, error } = await stripe.createPaymentMethod({
        type: "card",
        card: cardNumberElement,
        billing_details: { name: formData.cardHolderName, address: { postal_code: formData.zip } }
      })
      dispatch(failedPaymentMethods())

      if (error) {
        if (error.type === "card_error") {
          dispatch(receiveErrors({ data: { message: { card_error: error.message } }, showToast: false }))
        } else {
          toaster.error(t("booking.error_messages.stripe_error"))
        }
        return
      }

      const isNewUser = !clientId || clientId === "null" || clientId === "undefined"
      const res = await dispatch(
        attachUserPaymentMethod({
          new_user: isNewUser,
          id: clientId,
          payment_method_id: paymentMethod.id,
          guide_id: isNewUser ? guide?.id : undefined,
          client_attributes: {}
        })
      )
      const newMethodId = res?.payment_methods?.[0]?.payment_method_id
      if (newMethodId) {
        dispatch(setSelectedPaymentMethodId(newMethodId))
        if (clientId) dispatch(getPaymentMethods(clientId))
      }
      toaster.success(t("booking.stripe_added_success"))
      modalForm.resetFields()
      setIsModalVisible(false)
    } catch (error) {
      dispatch(failedPaymentMethods())
      console.error(error)
    } finally {
      setIsAddingCard(false)
    }
  }

  const paypalLinkAvailable = isPaypalConnected && isFeatureEnabled("PAYPAL_SUPPORT")
  const visibleTabs = [
    { key: PAYMENT_TABS.CARD, label: "Card", hidden: !isStripeConnected, disabled: !isStripeChargeable, tooltip: stripeProblemMessage },
    { key: PAYMENT_TABS.LINK, label: "Send Payment Link", hidden: !isStripeChargeable && !paypalLinkAvailable },
    { key: PAYMENT_TABS.CASH, label: "Cash" },
    { key: PAYMENT_TABS.OTHERS, label: "Others" }
  ].filter((tab) => !tab.hidden)

  const linkProviderLabel = linkProvider === LINK_PROVIDERS.STRIPE ? "Stripe" : "PayPal"
  const linkInfoText = linkProvider === LINK_PROVIDERS.STRIPE
    ? `A secure Stripe payment link will be sent${clientEmail ? ` to ${clientEmail}` : ""}. They can pay from any device.`
    : `A secure PayPal payment link will be sent${clientEmail ? ` to ${clientEmail}` : ""}. They can pay with their PayPal account or any card.`
  // Reset tip when switching away from card — tip is card-only
  const handleTabChange = (tab) => {
    if (tab !== PAYMENT_TABS.CARD) setTipAmount(0)
    setActiveTab(tab)
  }

  const amountLabel = activeTab === PAYMENT_TABS.CASH || activeTab === PAYMENT_TABS.OTHERS ? "Amount Received" : "Amount to Pay"
  const submitLabel = activeTab === PAYMENT_TABS.LINK ? `Send Payment Link via ${linkProviderLabel}` : t("global.submit")

  return {
    activeTab, setActiveTab: handleTabChange,
    linkProvider, setLinkProvider,
    tipAmount, setTipAmount: (value) => setTipAmount(parseFloat((Number(value) || 0).toFixed(2))),
    selectedCardId: selectedPaymentMethodId, setSelectedCardId: (id) => dispatch(setSelectedPaymentMethodId(id)),
    isModalVisible, setIsModalVisible,
    isAddingCard, isSubmitting,
    isStripeConnected, isPaypalConnected,
    isStripeChargeable, stripeProblemMessage,
    paymentMethods, paymentMethodsLoading,
    clientId,
    visibleTabs, linkProviderLabel, linkInfoText, amountLabel, submitLabel,
    handleSubmit, createPaymentMethod,
    feeSummaryData, feeSummaryLoading
  }
}
